import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LoginUser, reset } from "../features/authSlice";
import { motion } from "framer-motion";
import { HiArrowRight } from "react-icons/hi";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { MdCalendarToday } from "react-icons/md";

// ─── Decorative grid images (same as Hero in ProgramPage) ─────────────────────
const gridImages = [
  "https://i.ibb.co.com/VW6N2JDc/IMG-4567.jpg",
  "https://i.ibb.co.com/DPTHdVs2/gambar.jpg",
  "https://i.ibb.co.com/bRMTtTXd/Nutrition-education-and-food-donation-for-breastfeeding-and-pregnant-women.jpg",
  "https://i.ibb.co.com/zhN2KP84/IMG-0069.jpg",
  "https://i.ibb.co.com/ccxMsrBs/Vegetable-donation-buy-or-donate-program-to-the-Limpok-Village.jpg",
  "https://i.ibb.co.com/jPsSsKDZ/Donasi-sayur.jpg",
];

const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isError, isSuccess, isLoading, message } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (isSuccess) navigate("/dashboard");
    if (isError || isSuccess) {
      const timer = setTimeout(() => dispatch(reset()), 3000);
      return () => clearTimeout(timer);
    }
  }, [user, isSuccess, isError, dispatch, navigate]);

  const Auth = (e) => {
    e.preventDefault();
    dispatch(LoginUser({ username, password }));
  };

  return (
    <div className="min-h-screen bg-[#f7f3ec] flex flex-col lg:flex-row overflow-hidden font-homepage">

      {/* ─── Left Panel: Image Grid + Branding ──────────────────────────────── */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col overflow-hidden">
        {/* Amber left border line */}
        <div
          className="absolute left-0 top-0 h-full w-1 bg-[#c9862a] z-30"
          style={{
            transform: loaded ? "scaleY(1)" : "scaleY(0)",
            transformOrigin: "top",
            transition: "transform 1s cubic-bezier(0.16,1,0.3,1) 0.3s",
          }}
        />

        {/* Image grid 3x2 */}
        <div className="grid grid-cols-3 grid-rows-2 h-full">
          {gridImages.map((src, i) => (
            <div key={i} className="overflow-hidden">
              <img
                src={src}
                alt=""
                className="w-full h-full object-cover brightness-[0.55]"
              />
            </div>
          ))}
        </div>

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0d0c0a]/85 via-[#1a4a35]/60 to-[#0d0c0a]/40" />

        {/* Branding content */}
        <div className="absolute inset-0 flex flex-col justify-between p-12 z-20">
          {/* Logo */}
          <div
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? "translateY(0)" : "translateY(-16px)",
              transition: "all 0.8s ease 0.4s",
            }}
          >
            <div className="flex items-center gap-3">
              <div className="h-px w-10 bg-[#c9862a]" />
              <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
                Portal Admin
              </span>
            </div>
            <h2 className="font-serif text-3xl font-bold text-white mt-4 leading-tight">
              Rumoh Pangan<br />
              <span className="text-[#c9862a]">Aceh</span>
            </h2>
          </div>

          {/* Bottom tagline */}
          <div
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.9s ease 0.6s",
            }}
          >
            {/* Decorative diamond divider */}
            <div className="flex items-center gap-4 mb-8">
              <div className="flex-1 h-px bg-white/15" />
              <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
              <div className="flex-1 h-px bg-white/15" />
            </div>

            <p className="text-white/60 text-sm leading-relaxed max-w-xs mt-4">
              Platform manajemen program ketahanan pangan untuk masyarakat Aceh yang berkelanjutan.
            </p>

            <div className="mt-8 text-white/30 text-xs tracking-wide">
              © 2026 Rumoh Pangan Aceh
            </div>
          </div>
        </div>
      </div>

      {/* ─── Right Panel: Login Form ─────────────────────────────────────────── */}
      <div className="flex-1 lg:w-1/2 flex flex-col items-center justify-center px-6 py-12 relative overflow-hidden">

        {/* Decorative bg watermark */}
        <div className="absolute inset-0 flex items-center justify-center select-none pointer-events-none overflow-hidden">
          <span className="font-serif text-[200px] font-bold text-[#1a4a35]/[0.04] leading-none">
            RPA
          </span>
        </div>

        {/* Mobile logo (visible only on small screens) */}
        <div className="lg:hidden flex items-center gap-3 mb-10">
          <div className="h-px w-10 bg-[#c9862a]" />
          <span className="font-serif text-2xl font-bold text-[#1a4a35]">
            Rumoh Pangan <span className="text-[#c9862a]">Aceh</span>
          </span>
          <div className="h-px w-10 bg-[#c9862a]" />
        </div>

        <div
          className="w-full max-w-md relative z-10"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? "translateY(0)" : "translateY(24px)",
            transition: "all 0.9s ease 0.35s",
          }}
        >
          {/* Form header */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-10 bg-[#c9862a]" />
              <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
                Portal Admin
              </span>
            </div>
            <h2 className="font-serif text-4xl font-bold text-[#1a1a18] leading-tight">
              Masuk ke<br />Dashboard
            </h2>
            <p className="text-[#6b6456] text-sm mt-3 leading-relaxed">
              Kelola program dan data ketahanan pangan Aceh melalui portal admin Rumoh Pangan Aceh.
            </p>
          </div>

          {/* Thin amber divider */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 h-px bg-[#e8e0d0]" />
            <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
            <div className="flex-1 h-px bg-[#e8e0d0]" />
          </div>

          {/* Error message */}
          {isError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border-l-4 border-red-400 text-red-700 px-4 py-3 mb-6 text-sm font-medium"
            >
              {message}
            </motion.div>
          )}

          <form onSubmit={Auth} className="space-y-6">

            {/* Username */}
            <div>
              <label className="block text-xs font-bold text-[#3a3a34] tracking-widest uppercase mb-2">
                Username <span className="text-[#c9862a]">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#c9862a]">
                  <FaUser size={13} />
                </div>
                <input
                  name="username"
                  type="text"
                  required
                  placeholder="Masukkan username Anda"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-white border border-[#e8e0d0] text-[#1a1a18] text-sm px-4 py-3.5 pl-11 outline-none focus:border-[#c9862a] transition-colors duration-200 placeholder-[#b0a898]"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-bold text-[#3a3a34] tracking-widest uppercase mb-2">
                Password <span className="text-[#c9862a]">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#c9862a]">
                  <FaLock size={13} />
                </div>
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="Masukkan password Anda"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-[#e8e0d0] text-[#1a1a18] text-sm px-4 py-3.5 pl-11 pr-12 outline-none focus:border-[#c9862a] transition-colors duration-200 placeholder-[#b0a898]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#a09880] hover:text-[#c9862a] transition-colors duration-200"
                >
                  {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                </button>
              </div>
            </div>

            {/* Buttons row */}
            <div className="pt-2 flex gap-3">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="flex-1 flex items-center justify-center gap-2 border border-[#e8e0d0] hover:border-[#c9862a] text-[#6b6456] hover:text-[#c9862a] font-bold text-sm tracking-widest uppercase px-8 py-4 transition-colors duration-200"
              >
                <HiArrowRight size={15} className="rotate-180" />
                Kembali
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 flex items-center justify-center gap-3 bg-[#1a4a35] hover:bg-[#143d2b] disabled:bg-[#a09880] text-white font-bold text-sm tracking-widest uppercase px-8 py-4 transition-colors duration-200 group"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Memproses...
                  </>
                ) : (
                  <>
                    Masuk
                    <HiArrowRight
                      size={15}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </div>

          </form>

          {/* Bottom note */}
          <p className="text-center text-[#a09880] text-xs mt-8 leading-relaxed">
            Dengan masuk, Anda setuju untuk mengelola data program<br />
            secara bertanggung jawab demi ketahanan pangan Aceh.
          </p>

          {/* Mobile footer */}
          <p className="lg:hidden text-center text-[#c9a870]/50 text-xs mt-6">
            © 2026 Rumoh Pangan Aceh
          </p>
        </div>
      </div>

    </div>
  );
};

export default LoginPage;